import { createContext } from 'react';

/**
 * TutorialContext
 * 
 * Context for managing tutorial state and functionality across the application.
 * Provides access to tutorial progress, navigation, and state management.
 * 
 * This context is consumed by:
 * - useTutorial hook
 * - TutorialProvider component
 * 
 * The context value includes:
 * - State: isActive, isLoading, currentSectionId, currentStepId, etc.
 * - Actions: startTutorial, exitTutorial, nextStep, previousStep, etc.
 * - Computed: totalSteps, progress, isFirstStep, isLastStep, etc.
 */

// Default context value (used when context is accessed outside of provider)
const defaultContextValue = {
  // State
  isActive: false,
  isLoading: false,
  shouldAutoStart: false,
  mode: 'onboarding',
  pageId: null,
  currentSectionId: null,
  currentStepId: null,
  currentStepIndex: 0,
  tutorialData: null,
  tutorialSteps: [],
  flattenedSteps: [],
  patientDescription: null,
  isFullTutorial: false,
  
  // Actions
  setTutorialSteps: () => {
    console.warn('setTutorialSteps called outside of TutorialProvider');
  },
  startTutorial: () => {
    console.warn('startTutorial called outside of TutorialProvider');
  },
  exitTutorial: () => {
    console.warn('exitTutorial called outside of TutorialProvider');
  },
  completeTutorial: () => {
    console.warn('completeTutorial called outside of TutorialProvider');
  },
  nextStep: () => {
    console.warn('nextStep called outside of TutorialProvider');
  },
  previousStep: () => {
    console.warn('previousStep called outside of TutorialProvider');
  },
  goToStep: () => {
    console.warn('goToStep called outside of TutorialProvider');
  },
  /**
   * @param {string | null} [_description]
   * @returns {void}
   */
  setPatientDescription: (_description) => {
    console.warn('setPatientDescription called outside of TutorialProvider');
  },
  markPageTutorialViewed: () => {
    console.warn('markPageTutorialViewed called outside of TutorialProvider');
  },
  
  // Computed
  totalSteps: 0,
  progress: 0,
  isFirstStep: true,
  isLastStep: false,
  currentStep: null
};

export const TutorialContext = createContext(defaultContextValue);
